import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Copy, Check, Loader2 } from "lucide-react";
import { adminFetch } from "../adminFetch";
import { StatusPill } from "./StatusPill";

const STATUS_OPTIONS = [
    "new",
    "contacted",
    "qualified",
    "not_qualified",
    "closed",
    "archived",
];

const formatDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            timeZoneName: "short",
        });
    } catch (e) {
        return iso;
    }
};

const Row = ({ label, value, testId }) => (
    <div className="grid grid-cols-1 gap-1 sm:grid-cols-[180px_1fr]" data-testid={testId}>
        <span className="text-[11px] uppercase tracking-widest-2 text-text-muted">
            {label}
        </span>
        <span className="break-words text-[14px] text-text-primary">
            {value === null || value === undefined || value === "" ? (
                <span className="text-text-muted">—</span>
            ) : (
                value
            )}
        </span>
    </div>
);

const CopyField = ({ value, label }) => {
    const [copied, setCopied] = useState(false);
    if (!value) return <span className="text-text-muted">—</span>;
    const onCopy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (e) {
            /* ignore */
        }
    };
    return (
        <button
            type="button"
            onClick={onCopy}
            className="group inline-flex items-center gap-2 text-text-primary hover:text-[var(--accent)] transition-colors"
            data-testid={`detail-copy-${label}`}
        >
            <span>{value}</span>
            {copied ? (
                <Check className="h-3.5 w-3.5 text-[var(--accent)]" strokeWidth={2} />
            ) : (
                <Copy
                    className="h-3.5 w-3.5 opacity-50 group-hover:opacity-100"
                    strokeWidth={1.5}
                />
            )}
        </button>
    );
};

const Section = ({ title, children }) => (
    <div>
        <h3 className="text-[11px] uppercase tracking-widest-2 text-text-muted">
            {title}
        </h3>
        <div className="mt-4 space-y-4 border-t border-border-subtle pt-4">
            {children}
        </div>
    </div>
);

export const LeadDetailDrawer = ({ row, onClose, onStatusChanged }) => {
    const [doc, setDoc] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [savedFlash, setSavedFlash] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState("new");
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!row) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await adminFetch(
                    `/admin/leads/${row.id}?source=${row.source}`,
                );
                if (cancelled) return;
                setDoc(data.item);
                setSelectedStatus(data.item.status || "new");
            } catch (e) {
                if (!cancelled) setError(e.message || "Failed to load lead.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [row]);

    // ESC to close
    useEffect(() => {
        if (!row) return;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [row, onClose]);

    const onSaveStatus = async () => {
        if (!doc) return;
        setSaving(true);
        try {
            const updated = await adminFetch(
                `/admin/leads/${doc.id}`,
                {
                    method: "PATCH",
                    body: JSON.stringify({
                        status: selectedStatus,
                        source: row.source,
                    }),
                },
            );
            setDoc(updated.item);
            setSavedFlash(true);
            setTimeout(() => setSavedFlash(false), 1600);
            onStatusChanged && onStatusChanged(doc.id, selectedStatus);
        } catch (e) {
            setError(e.message || "Save failed.");
        } finally {
            setSaving(false);
        }
    };

    const division = useMemo(() => row?.division, [row]);

    return (
        <AnimatePresence>
            {row && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="fixed inset-0 z-50"
                    data-testid="lead-detail-drawer"
                >
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-[rgba(0,0,0,0.55)]"
                        onClick={onClose}
                        data-testid="lead-drawer-backdrop"
                    />
                    {/* Drawer */}
                    <motion.aside
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
                        className="absolute right-0 top-0 h-full w-full overflow-y-auto border-l border-border-subtle bg-base sm:max-w-2xl"
                        data-testid="lead-drawer"
                    >
                        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border-subtle bg-[rgba(10,10,11,0.85)] backdrop-blur-[14px] px-6 py-4">
                            <div className="min-w-0">
                                <p className="text-[11px] uppercase tracking-widest-2 text-text-muted">
                                    Lead Detail
                                </p>
                                <p className="truncate font-display text-[18px] font-semibold text-text-primary">
                                    {doc?.full_name ||
                                        doc?.owner_name ||
                                        doc?.contact_name ||
                                        doc?.name ||
                                        "—"}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                aria-label="Close"
                                data-testid="lead-drawer-close"
                                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border-subtle text-text-secondary hover:text-text-primary hover:border-border-default"
                            >
                                <X className="h-4 w-4" strokeWidth={1.5} />
                            </button>
                        </div>

                        {/* Status changer */}
                        <div className="border-b border-border-subtle px-6 py-4 flex flex-wrap items-center gap-3">
                            <span className="text-[11px] uppercase tracking-widest-2 text-text-muted">
                                Status
                            </span>
                            <StatusPill status={doc?.status || "new"} />
                            <select
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                                data-testid="lead-status-select"
                                className="appearance-none rounded-lg border border-border-subtle bg-[var(--bg-elevated-2)] px-3 py-1.5 text-[13px] text-text-primary focus:outline-none focus:border-[rgba(124,255,79,0.55)]"
                            >
                                {STATUS_OPTIONS.map((s) => (
                                    <option key={s} value={s}>
                                        {s.replace("_", " ")}
                                    </option>
                                ))}
                            </select>
                            <button
                                type="button"
                                onClick={onSaveStatus}
                                disabled={saving || selectedStatus === (doc?.status || "new")}
                                data-testid="lead-status-save"
                                className="inline-flex items-center gap-2 rounded-lg bg-[var(--accent)] px-4 py-1.5 text-[13px] font-medium text-[#0a0a0b] hover:brightness-110 disabled:opacity-60 disabled:pointer-events-none"
                            >
                                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                                Save
                            </button>
                            {savedFlash && (
                                <span
                                    className="inline-flex items-center gap-1.5 text-[12.5px] text-[var(--accent)]"
                                    data-testid="lead-status-saved"
                                >
                                    <Check className="h-3 w-3" strokeWidth={2.5} />
                                    Saved
                                </span>
                            )}
                            {error && (
                                <span className="text-[12.5px] text-red-400/90">
                                    {error}
                                </span>
                            )}
                        </div>

                        {/* Body */}
                        {loading && (
                            <p className="px-6 py-10 text-center text-[13px] text-text-muted">
                                Loading…
                            </p>
                        )}
                        {!loading && doc && (
                            <div className="space-y-8 px-6 py-6">
                                <Section title="Contact">
                                    <Row
                                        label="Name"
                                        value={
                                            doc.full_name ||
                                            doc.owner_name ||
                                            doc.contact_name ||
                                            doc.name
                                        }
                                    />
                                    <Row
                                        label="Email"
                                        value={
                                            <CopyField
                                                value={doc.email}
                                                label="email"
                                            />
                                        }
                                    />
                                    <Row
                                        label="Phone"
                                        value={
                                            <CopyField
                                                value={doc.phone}
                                                label="phone"
                                            />
                                        }
                                    />
                                </Section>

                                <Section title="Business / Property">
                                    <Row
                                        label="Business"
                                        value={
                                            doc.business_name || doc.company_name
                                        }
                                    />
                                    <Row label="City" value={doc.city} />
                                    <Row label="State" value={doc.state} />
                                    {doc.industry && (
                                        <Row
                                            label="Industry"
                                            value={
                                                doc.industry === "Other" &&
                                                doc.industry_other
                                                    ? `${doc.industry} — ${doc.industry_other}`
                                                    : doc.industry
                                            }
                                        />
                                    )}
                                    {doc.property_type && (
                                        <Row
                                            label="Property Type"
                                            value={doc.property_type}
                                        />
                                    )}
                                    {doc.property_address && (
                                        <Row
                                            label="Property Address"
                                            value={doc.property_address}
                                        />
                                    )}
                                    {doc.material_type && (
                                        <Row
                                            label="Material Type"
                                            value={doc.material_type}
                                        />
                                    )}
                                    {doc.estimated_volume && (
                                        <Row
                                            label="Volume"
                                            value={doc.estimated_volume}
                                        />
                                    )}
                                    {doc.delivery_location && (
                                        <Row
                                            label="Delivery"
                                            value={doc.delivery_location}
                                        />
                                    )}
                                    {doc.area_of_interest && (
                                        <Row
                                            label="Area"
                                            value={doc.area_of_interest}
                                        />
                                    )}
                                    {doc.company_size && (
                                        <Row
                                            label="Company Size"
                                            value={doc.company_size}
                                        />
                                    )}
                                    {doc.time_in_business && (
                                        <Row
                                            label="Time in Business"
                                            value={doc.time_in_business}
                                        />
                                    )}
                                </Section>

                                {division === "funding" && (
                                    <Section title="Funding Profile">
                                        <Row
                                            label="Amount Looking For"
                                            value={doc.amount_looking_for}
                                        />
                                        <Row
                                            label="Monthly Revenue"
                                            value={doc.monthly_revenue}
                                        />
                                        <Row
                                            label="Timeline"
                                            value={doc.timeline}
                                        />
                                        <Row
                                            label="Use of Funds"
                                            value={doc.use_of_funds}
                                        />
                                    </Section>
                                )}

                                <Section title="Notes">
                                    <p className="text-[14px] leading-relaxed text-text-primary">
                                        {doc.notes ||
                                            doc.description ||
                                            doc.message ||
                                            doc.project_notes ||
                                            doc.reason_for_selling || (
                                                <span className="text-text-muted">—</span>
                                            )}
                                    </p>
                                </Section>

                                <Section title="Attribution">
                                    <Row label="Source Page" value={doc.source_page} />
                                    <Row label="UTM Source" value={doc.utm_source} />
                                    <Row label="UTM Medium" value={doc.utm_medium} />
                                    <Row label="UTM Campaign" value={doc.utm_campaign} />
                                    <Row label="UTM Content" value={doc.utm_content} />
                                    <Row label="UTM Term" value={doc.utm_term} />
                                    <Row label="Referrer" value={doc.referrer} />
                                    <Row label="Landing Path" value={doc.landing_path} />
                                </Section>

                                <Section title="System">
                                    <Row label="Lead ID" value={<code className="text-[12px]">{doc.id}</code>} />
                                    <Row label="Source Collection" value={row.source} />
                                    <Row label="Submitted At" value={formatDate(doc.created_at)} />
                                    {doc.status_updated_at && (
                                        <Row
                                            label="Status Updated At"
                                            value={formatDate(doc.status_updated_at)}
                                        />
                                    )}
                                    <Row
                                        label="Email Notification"
                                        value={doc.email_notification_status}
                                    />
                                    {doc.onesignal_response_id && (
                                        <Row
                                            label="OneSignal Response ID"
                                            value={<code className="text-[12px]">{doc.onesignal_response_id}</code>}
                                        />
                                    )}
                                    {doc.email_notification_error && (
                                        <Row
                                            label="Email Error"
                                            value={doc.email_notification_error}
                                        />
                                    )}
                                    <Row label="IP Address" value={doc.ip_address} />
                                    <Row label="User Agent" value={
                                        <span className="break-all text-[12.5px] text-text-secondary">
                                            {doc.user_agent || "—"}
                                        </span>
                                    } />
                                </Section>
                            </div>
                        )}
                    </motion.aside>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default LeadDetailDrawer;
